"use client";

import { Loader2, LogOut, ShieldAlert } from "lucide-react";
import { useRouter } from "next/navigation";
import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useMe } from "@/hooks/use-me";
import { getSupabase } from "@/lib/supabase/client";

/**
 * Sadece `user_profiles.is_super_admin = true` kullanıcıyı içeri alır.
 * Backend `require_super_admin` zaten endpoint'leri kilitliyor — bu, UX
 * tarafı: yetkisiz kullanıcı sidebar'ı bile görmesin.
 */
export function SuperAdminGuard({ children }: { children: ReactNode }) {
  const router = useRouter();
  const me = useMe();

  const signOut = async () => {
    try {
      await getSupabase().auth.signOut();
    } catch {
      /* ignore */
    }
    router.replace("/login");
  };

  if (me.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="size-5 animate-spin text-text-tertiary" />
      </div>
    );
  }

  if (!me.data?.profile?.is_super_admin) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-bg-page p-8">
        <Card className="max-w-md">
          <CardContent className="flex flex-col items-center gap-4 p-10 text-center">
            <ShieldAlert className="size-10 text-text-tertiary" />
            <div className="space-y-2">
              <p className="font-display text-xl font-semibold">CMS'e erişim yok</p>
              <p className="text-sm text-text-secondary">
                Bu panel sadece sistem yöneticileri içindir. Hesabında
                <span className="font-mono"> is_super_admin = true </span>
                işareti yok. Operatöre bildir veya kullanıcı paneline dön.
              </p>
            </div>
            <Button onClick={signOut} variant="outline" className="mt-2 w-full">
              <LogOut className="mr-2 size-4" />
              Çıkış yap
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}
